/**
 * @fileoverview Lesson-assessments-history modal state for the final drawer (AR-579).
 *
 * Mirrors the legacy `handleHistory` flow (`StudentModalContent`): open the
 * shared `AssessmentHistoryModal` per student + lv4 item, fetching from the
 * legacy `POST /api/projects/:id/partial_evaluations` endpoint through
 * `fetchAssessmentsAverageHistory`. That service uses the global axios
 * instance, so the response stays snake_case — exactly what the shared modal
 * and `ILessonAssessmentHistory` expect (never route this through
 * `helpers/api`: humps would camelize the payload and break the modal).
 * `t` is captured via a ref to avoid the unstable-`t` dep loop
 * (see feedback_useArcTranslation_deps).
 */

import { useCallback, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { IconX } from '@tabler/icons-react'
import { notifications } from 'context'
import { fetchAssessmentsAverageHistory } from '@legacy/pages/FinalAssessment/service'
import type { ILessonAssessmentHistory } from 'interfaces'
import type { FinalDrawerStudent } from './types'

export function useLessonAssessmentsHistory(projectId: string) {
  const { t } = useTranslation()
  const tRef = useRef(t)
  tRef.current = t

  const [opened, setOpened] = useState(false)
  const [loading, setLoading] = useState(false)
  const [entries, setEntries] = useState<ILessonAssessmentHistory[]>([])
  const [student, setStudent] = useState<FinalDrawerStudent | null>(null)

  const closeHistory = useCallback(() => setOpened(false), [])

  const openHistory = useCallback(
    async (nextStudent: FinalDrawerStudent, lv4ItemId: string) => {
      setEntries([])
      setStudent(nextStudent)
      setLoading(true)
      setOpened(true)
      try {
        const history = await fetchAssessmentsAverageHistory(projectId, nextStudent.id, lv4ItemId)
        setEntries(history.session_meetings as ILessonAssessmentHistory[])
      } catch {
        setOpened(false)
        notifications.show({
          message: tRef.current('arc_teacher_assessment_drawer.error.load_failed'),
          color: 'red',
          icon: <IconX size={18} />,
        })
      } finally {
        setLoading(false)
      }
    },
    [projectId],
  )

  return { opened, loading, entries, student, openHistory, closeHistory }
}
